<template>
  <el-drawer
    :visible.sync="innerVisible"
    direction="rtl"
    size="88%"
    append-to-body
    :wrapper-closable="true"
    custom-class="device-push-drawer"
    :with-header="false"
    @open="loadData"
    @closed="onClosed"
  >
    <div class="push-wrap">
      <div class="push-head">
        <div>
          <div class="push-title">第三方 API 推送</div>
          <div class="push-sub">{{ deviceLabel }}</div>
        </div>
        <el-button type="text" icon="el-icon-close" @click="innerVisible = false" />
      </div>

      <div class="push-toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">新增第三方</el-button>
        <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
      </div>

      <el-table :data="configs" v-loading="loading" border size="small" class="push-table">
        <el-table-column prop="pushName" label="名称" min-width="110" show-overflow-tooltip />
        <el-table-column prop="apiUrl" label="API地址" min-width="190" show-overflow-tooltip />
        <el-table-column label="间隔" width="82" align="center">
          <template slot-scope="{ row }">{{ row.intervalSeconds || 0 }}秒</template>
        </el-table-column>
        <el-table-column label="状态" width="74" align="center">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.enabled === '0' ? 'success' : 'info'">
              {{ row.enabled === '0' ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="最近结果" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span :class="row.lastStatus === 'failed' ? 'status-failed' : 'status-ok'">
              {{ statusText(row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openEdit(row)">修改</el-button>
            <el-button type="text" size="small" :loading="pushingId === row.pushId" @click="pushNow(row)">立即推送</el-button>
            <el-button type="text" size="small" class="danger" @click="remove(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="formVisible" class="push-form-panel">
        <div class="form-title">{{ form.pushId ? '修改推送配置' : '新增推送配置' }}</div>
        <el-form :model="form" label-width="108px" size="small">
          <el-form-item label="第三方名称" required>
            <el-input v-model="form.pushName" placeholder="如 客户平台、监管平台" />
          </el-form-item>
          <el-form-item label="API地址" required>
            <el-input v-model="form.apiUrl" placeholder="https://example.com/api/gas/data" />
          </el-form-item>
          <el-form-item label="推送间隔" required>
            <el-input-number v-model="form.intervalSeconds" :min="10" :step="10" controls-position="right" />
            <span class="unit-text">秒</span>
          </el-form-item>
          <el-form-item label="启用状态">
            <el-switch v-model="formEnabled" active-text="启用" inactive-text="停用" />
          </el-form-item>
          <el-form-item label="推送内容" required>
            <el-checkbox-group v-model="form.contentFields" class="field-grid">
              <el-checkbox v-for="item in metricOptions" :key="item.key" :label="item.key">
                {{ item.label }}
              </el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-form-item label="请求头JSON">
            <el-input
              v-model="form.headersJson"
              type="textarea"
              :rows="3"
              placeholder='{"Authorization":"Bearer token"}'
            />
          </el-form-item>
        </el-form>
        <div class="form-actions">
          <el-button size="small" @click="formVisible = false">取消</el-button>
          <el-button type="primary" size="small" :loading="saving" @click="save">保存配置</el-button>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { gasApi } from '@/api/gas'
import { getDeviceTypeMetrics } from '@/config/deviceTypeMetrics'

export default {
  name: 'DevicePushDrawer',
  props: {
    visible: { type: Boolean, default: false },
    device: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      saving: false,
      pushingId: null,
      configs: [],
      formVisible: false,
      form: this.emptyForm()
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(v) {
        this.$emit('update:visible', v)
      }
    },
    deviceLabel() {
      if (!this.device) return '未选择设备'
      const code = this.device.deviceCode ? `（${this.device.deviceCode}）` : ''
      const type = this.device.terminalType ? ` · ${this.device.terminalType}` : ''
      return `${this.device.deviceName || '未命名设备'}${code}${type}`
    },
    formEnabled: {
      get() {
        return this.form.enabled === '0'
      },
      set(v) {
        this.form.enabled = v ? '0' : '1'
      }
    },
    metricOptions() {
      const type = (this.device && (this.device.terminalType || this.device.deviceType)) || ''
      return getDeviceTypeMetrics(type)
    }
  },
  watch: {
    device() {
      if (this.visible) this.loadData()
    }
  },
  methods: {
    emptyForm() {
      return {
        pushId: null,
        deviceId: null,
        pushName: '',
        apiUrl: '',
        intervalSeconds: 300,
        enabled: '0',
        contentFields: [],
        headersJson: ''
      }
    },
    defaultFields() {
      return this.metricOptions.map(item => item.key)
    },
    async loadData() {
      if (!this.device || !this.device.deviceId) return
      this.loading = true
      try {
        const res = await gasApi.device.pushConfigs(this.device.deviceId)
        this.configs = res.data || []
      } finally {
        this.loading = false
      }
    },
    onClosed() {
      this.configs = []
      this.formVisible = false
      this.form = this.emptyForm()
    },
    openCreate() {
      if (!this.device || !this.device.deviceId) return
      this.form = {
        ...this.emptyForm(),
        deviceId: this.device.deviceId,
        pushName: '第三方推送',
        contentFields: this.defaultFields()
      }
      this.formVisible = true
    },
    openEdit(row) {
      const allowed = new Set(this.metricOptions.map(item => item.key))
      this.form = {
        ...this.emptyForm(),
        ...row,
        contentFields: this.parseFields(row.contentFieldsJson).filter(key => allowed.has(key))
      }
      if (!this.form.contentFields.length) this.form.contentFields = this.defaultFields()
      this.formVisible = true
    },
    parseFields(raw) {
      if (!raw) return []
      try {
        const arr = JSON.parse(raw)
        return Array.isArray(arr) ? arr : []
      } catch (e) {
        return String(raw).split(',').map(s => s.trim()).filter(Boolean)
      }
    },
    async save() {
      if (!this.form.pushName) return this.$message.warning('请填写第三方名称')
      if (!this.form.apiUrl) return this.$message.warning('请填写API地址')
      if (!this.form.contentFields.length) return this.$message.warning('请选择推送内容')
      if (this.form.headersJson) {
        try {
          JSON.parse(this.form.headersJson)
        } catch (e) {
          return this.$message.warning('请求头JSON格式不正确')
        }
      }
      this.saving = true
      try {
        await gasApi.device.savePushConfig({
          ...this.form,
          deviceId: this.device.deviceId,
          contentFieldsJson: JSON.stringify(this.form.contentFields)
        })
        this.$message.success('保存成功')
        this.formVisible = false
        this.loadData()
      } finally {
        this.saving = false
      }
    },
    async pushNow(row) {
      this.pushingId = row.pushId
      try {
        const res = await gasApi.device.pushNow(row.pushId)
        const d = res.data || {}
        if (d.success) this.$message.success('推送成功')
        else this.$message.warning(d.message || '推送失败')
        this.loadData()
      } finally {
        this.pushingId = null
      }
    },
    async remove(row) {
      await this.$confirm('确认删除该第三方推送配置？', '提示', { type: 'warning' })
      await gasApi.device.removePushConfig(row.pushId)
      this.$message.success('删除成功')
      this.loadData()
    },
    statusText(row) {
      if (!row.lastStatus) return '未推送'
      const label = row.lastStatus === 'success' ? '成功' : '失败'
      return row.lastMessage ? `${label}：${row.lastMessage}` : label
    }
  }
}
</script>

<style scoped>
.push-wrap {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 18px 20px;
  box-sizing: border-box;
  overflow: auto;
}
.push-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}
.push-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}
.push-sub {
  margin-top: 4px;
  color: #86909c;
  font-size: 13px;
}
.push-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.push-table {
  flex-shrink: 0;
}
.status-ok {
  color: #00b42a;
}
.status-failed,
.danger {
  color: #f53f3f;
}
.push-form-panel {
  margin-top: 16px;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 14px 16px;
  background: #fff;
}
.form-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  margin-bottom: 12px;
}
.unit-text {
  margin-left: 8px;
  color: #4e5969;
}
.field-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2px 8px;
}
.field-grid >>> .el-checkbox {
  margin-right: 0;
}
.form-actions {
  text-align: right;
}
</style>

<style>
.device-push-drawer .el-drawer__body {
  height: 100%;
  overflow: hidden;
}
</style>
